import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ParentLayout from '../components/ParentLayout';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { FileText, Download, Shield, ArrowLeft, ChevronDown, Save, Printer } from 'lucide-react';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Park City Chiefs Logo
const PCC_LOGO = "https://customer-assets.emergentagent.com/job_tesa-pcc-build/artifacts/ahkhjrnt_image.png";
// YMCA Logo  
const YMCA_LOGO = "https://upload.wikimedia.org/wikipedia/en/thumb/a/a3/YMCA_logo.svg/200px-YMCA_logo.svg.png";

const FORMS = {
  'background-check': {
    title: 'Background Check Authorization',
    description: 'YMCA Background Check Notice and Authorization form',
    url: `${API_URL}/api/forms/background-check`,
    color: 'red'
  },
  'volunteer-application': {
    title: 'Volunteer Application',
    description: 'YMCA Volunteer Application form',
    url: `${API_URL}/api/forms/volunteer-application`,
    color: 'blue'
  }
};

export default function BackgroundCheckPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeForm, setActiveForm] = useState(searchParams.get('form') || null);
  const [formDropdownOpen, setFormDropdownOpen] = useState(false);

  // Create the default layout plugin instance
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [],
    toolbarPlugin: {
      fullScreenPlugin: {
        onEnterFullScreen: (zoom) => {
          zoom(1);
        },
        onExitFullScreen: (zoom) => {
          zoom(1);
        },
      },
    },
  });

  const handleFormSelect = (formKey) => {
    setActiveForm(formKey);
    setFormDropdownOpen(false);
  };

  const handleDownload = () => {
    if (activeForm && FORMS[activeForm]) {
      window.open(FORMS[activeForm].url, '_blank');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!activeForm) {
    // Form selection screen
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          {/* Partnership Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <img src={PCC_LOGO} alt="Park City Chiefs" className="h-20 object-contain" />
              <div className="text-center">
                <p className="text-gray-500 text-sm font-medium">IN PARTNERSHIP WITH</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <Shield className="w-5 h-5 text-[#c41e3a]" />
                  <span className="text-lg font-bold text-[#32425c]">YMCA Background Check Program</span>
                </div>
              </div>
              <img src={YMCA_LOGO} alt="YMCA" className="h-16 object-contain" />
            </div>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <p className="text-[#fae36c] text-sm font-bold tracking-widest uppercase mb-1">COACH APPLICATION</p>
            <h1 className="text-3xl md:text-4xl font-display text-[#32425c] uppercase tracking-wide">
              BACKGROUND CHECK FORMS
            </h1>
            <p className="text-gray-600 mt-2">
              Select a form to fill out directly in your browser. You can save or print when complete.
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-blue-800 mb-3">How It Works</h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-700 text-sm">
              <li>Select a form below to open it in the viewer</li>
              <li>Click on any field to type your information</li>
              <li>Use the toolbar to zoom, navigate pages, or print</li>
              <li>Download or print your completed form</li>
              <li>Submit to the YMCA for processing</li>
            </ol>
          </div>

          {/* Form Selection Cards */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Background Check Form */}
            <button
              onClick={() => handleFormSelect('background-check')}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:border-[#c41e3a] transition text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition">
                  <FileText className="w-7 h-7 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#32425c] text-lg mb-1 group-hover:text-[#c41e3a] transition">
                    Background Check Authorization
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Required authorization for criminal history and registry checks.
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#c41e3a] text-sm font-medium">
                    Open Form →
                  </span>
                </div>
              </div>
            </button>

            {/* Volunteer Application Form */}
            <button
              onClick={() => handleFormSelect('volunteer-application')}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:border-[#32425c] transition text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition">
                  <FileText className="w-7 h-7 text-[#32425c]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#32425c] text-lg mb-1 group-hover:text-blue-700 transition">
                    Volunteer Application
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Personal information, availability, and volunteer interests.
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#32425c] text-sm font-medium">
                    Open Form →
                  </span>
                </div>
              </div>
            </button>
          </div>

          {/* Submission Info */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-xl p-6">
            <h3 className="font-semibold text-gray-800 mb-3">After Completing Forms</h3>
            <p className="text-gray-600 text-sm">
              Once you've filled out both forms, download or print them and submit to the YMCA. 
              Contact us at <a href="mailto:coaches@parkcitychiefs.org" className="text-[#c41e3a] underline">coaches@parkcitychiefs.org</a> if you need assistance.
            </p>
          </div>
        </div>
      </ParentLayout>
    );
  }

  // PDF Viewer screen
  const currentForm = FORMS[activeForm];

  return (
    <ParentLayout>
      <div className="h-[calc(100vh-80px)] flex flex-col">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveForm(null)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Forms</span>
            </button>
            
            {/* Form Selector Dropdown */}
            <div className="relative">
              <button
                onClick={() => setFormDropdownOpen(!formDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                <FileText className="w-4 h-4" />
                <span className="font-medium text-sm">{currentForm.title}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${formDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {formDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {Object.entries(FORMS).map(([key, form]) => (
                    <button
                      key={key}
                      onClick={() => handleFormSelect(key)}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition ${
                        key === activeForm ? 'bg-gray-100 font-medium' : ''
                      }`}
                    >
                      {form.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-[#c41e3a] text-white rounded-lg hover:bg-[#a01830] transition text-sm"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download</span>
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 bg-gray-100">
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <div style={{ height: '100%' }}>
              <Viewer
                fileUrl={currentForm.url}
                plugins={[defaultLayoutPluginInstance]}
                defaultScale={1}
                theme={{
                  theme: 'light',
                }}
              />
            </div>
          </Worker>
        </div>

        {/* Form switcher hint */}
        <div className="bg-[#32425c] text-white px-4 py-2 text-center text-sm flex-shrink-0">
          <span className="opacity-80">Tip: Fill out all fields, then use </span>
          <strong>Print</strong>
          <span className="opacity-80"> or </span>
          <strong>Download</strong>
          <span className="opacity-80"> to save your completed form.</span>
        </div>
      </div>

      {/* Dropdown overlay */}
      {formDropdownOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setFormDropdownOpen(false)}
        />
      )}
    </ParentLayout>
  );
}
